import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { usePackages, BASE_PRICES } from '../contexts/PackagesContext';
import { Crown } from 'lucide-react';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-25605">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const {
    user
  } = useUser();
  const {
    getPackagePrice
  } = usePackages();
  // Subscription discount for astrologers
  const isAstrologer = user?.role === 'astrologer';
  const currentPlan = isAstrologer ? 'PRO' : 'FREE'; // In real app, from SubscriptionContext
  const subDiscount = currentPlan === 'TOP' ? 20 : currentPlan === 'PRO' ? 10 : 0;
  const getDiscountedPackagePrice = (basePrice: number, type: 20 | 50 | 100) => {
    if (subDiscount > 0) {
      return getPackagePrice(type, subDiscount);
    }
    return basePrice;
  };
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }, ...(subDiscount > 0 ? [{
        label: `Скидка ${subDiscount}% (${currentPlan})` as string,
        variant: 'pro' as const
      }] : [])]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-25606">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-25607">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-25608" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-25609" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-25610" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-25611">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-25612" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-25613">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-25614">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-25615">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-25616">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-25617">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-25618">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-25619" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-25620">
                <div className="flex items-center" data-id="element-25621">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-25622" />
                  <span data-id="element-25623">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-25624">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-25625" />
                  <span data-id="element-25626">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-25627">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-25628" />
                  <span data-id="element-25629">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-25630">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25631">
          <div className="text-center mb-10" data-id="element-25632">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25633">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-25634">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-25635">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-25636" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-25637" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-25638">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25639" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25640" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25641" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-25642">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-25643">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-25644">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-25645">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-25646">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-25647">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-25648">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-25649">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25650">
            <div className="text-center mb-10" data-id="element-25651">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25652">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-25653">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25654">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-25655">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-25656">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-25657" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25658">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-25659">
                      <Moon className="w-5 h-5" data-id="element-25660" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25661">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25662">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25663">
                    <div className="text-xs text-slate-500" data-id="element-25664">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25665">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25666">
                    <div className="flex items-center justify-between mb-2" data-id="element-25667">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25668">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25669">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25670">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25671">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25672" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25673" />
                  <div className="mb-5 flex-grow" data-id="element-25674">
                    <div className="flex items-center justify-between mb-2" data-id="element-25675">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-25676">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-25677">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25678">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25679">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25680" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25681">
                    <Button fullWidth onClick={() => {
                    addItem({
                      id: `premium-natal-${Date.now()}`,
                      name: 'Premium отчёт: Натальная карта',
                      description: 'Полный профессиональный отчёт с глубоким анализом.',
                      price: 3900,
                      type: 'premium-report',
                      serviceName: 'Натальная карта',
                      format: 'PDF-отчёт',
                      duration: '30-40 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-25682">
                      Купить за 3 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-natal')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25683">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25684" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-25685">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-25686">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-25687" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25688">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-25689">
                      <Sun className="w-5 h-5" data-id="element-25690" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25691">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25692">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25693">
                    <div className="text-xs text-slate-500" data-id="element-25694">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25695">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25696">
                    <div className="flex items-center justify-between mb-2" data-id="element-25697">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25698">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25699">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25700">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25701">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25702" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25703" />
                  <div className="mb-5 flex-grow" data-id="element-25704">
                    <div className="flex items-center justify-between mb-2" data-id="element-25705">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-25706">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-25707">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25708">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25709">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25710" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25711">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-solar-${Date.now()}`,
                      name: 'Premium отчёт: Соляр',
                      description: 'Полный астрологический прогноз на личный год.',
                      price: 4400,
                      type: 'premium-report',
                      serviceName: 'Соляр',
                      format: 'PDF-отчёт',
                      duration: '25-35 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-25712">
                      Купить за 4 400 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-solar')} className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25713">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25714" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-25715">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-25716">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-25717" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-25718">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-25719">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-25720">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-25721">
                      <Layers className="w-5 h-5" data-id="element-25722" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-25723">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25724">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-25725">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-25726">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-25727">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-25728">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-25729">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25730">
                    <div className="flex items-center justify-between mb-2" data-id="element-25731">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25732">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25733">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25734">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25735">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25736" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25737" />
                  <div className="mb-5 flex-grow" data-id="element-25738">
                    <div className="flex items-center justify-between mb-2" data-id="element-25739">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-25740">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-25741">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25742">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25743">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25744" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25745">
                    <Button variant="primary" fullWidth onClick={() => {
                    addItem({
                      id: `premium-combo-${Date.now()}`,
                      name: 'Premium отчёт: Комбо (Натал + Соляр)',
                      description: 'Полный анализ личности и прогноз на ближайший год.',
                      price: 6900,
                      type: 'premium-report',
                      serviceName: 'Комбо: Натал + Соляр',
                      format: 'PDF-отчёт',
                      duration: '50-60 страниц',
                      oldPrice: 8300,
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} className="bg-violet-600 hover:bg-violet-700" data-id="element-25746">
                      Купить за 6 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-combo')} className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25747">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25748" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-25749">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-25750">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-25751" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25752">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-25753">
                      <Heart className="w-5 h-5" data-id="element-25754" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25755">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25756">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25757">
                    <div className="text-xs text-slate-500" data-id="element-25758">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25759">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25760">
                    <div className="flex items-center justify-between mb-2" data-id="element-25761">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25762">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25763">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25764">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25765">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25766" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25767" />
                  <div className="mb-5 flex-grow" data-id="element-25768">
                    <div className="flex items-center justify-between mb-2" data-id="element-25769">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-25770">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-25771">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25772">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25773">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25774" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25775">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-compatibility-${Date.now()}`,
                      name: 'Premium отчёт: Совместимость',
                      description: 'Полный анализ совместимости двух людей.',
                      price: 4900,
                      type: 'premium-report',
                      serviceName: 'Совместимость',
                      format: 'PDF-отчёт',
                      duration: '30-40 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-25776">
                      Купить за 4 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-compatibility')} className="text-sm text-rose-500 hover:text-rose-600 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25777">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25778" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-25779">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-25780">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-25781" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25782">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-25783">
                      <Activity className="w-5 h-5" data-id="element-25784" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25785">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25786">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25787">
                    <div className="text-xs text-slate-500" data-id="element-25788">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25789">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25790">
                    <div className="flex items-center justify-between mb-2" data-id="element-25791">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25792">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25793">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25794">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25795">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25796" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25797" />
                  <div className="mb-5 flex-grow" data-id="element-25798">
                    <div className="flex items-center justify-between mb-2" data-id="element-25799">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-25800">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-25801">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25802">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25803">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25804" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25805">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-forecast-${Date.now()}`,
                      name: 'Premium отчёт: Прогноз (Транзиты)',
                      description: 'Полный астрологический прогноз на ближайшие месяцы.',
                      price: 3900,
                      type: 'premium-report',
                      serviceName: 'Прогноз (Транзиты)',
                      format: 'PDF-отчёт',
                      duration: '20-30 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-25806">
                      Купить за 3 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-forecast')} className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25807">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25808" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-25809">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-25810">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-25811" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25812">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-25813">
                      <Video className="w-5 h-5" data-id="element-25814" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25815">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25816">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25817">
                    <div className="text-xs text-slate-500" data-id="element-25818">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25819">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25820">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25821">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-25822">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25823" />
                  <div className="mb-5 flex-grow" data-id="element-25824">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-25825">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-25826">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25827">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25828" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-25829">
                    <Button variant="outline" fullWidth onClick={() => onNavigate('astrologers')} data-id="element-25830">
                      Выбрать астролога
                    </Button>
                    <button onClick={() => onNavigate('service-consultation')} className="text-sm text-sky-600 hover:text-sky-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-25831">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-25832" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 4.5 ПАКЕТЫ ОТЧЕТОВ */}
      <AnimatedSection className="py-10 sm:py-14 lg:py-16 bg-slate-50 border-t border-slate-200" data-id="element-25833">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25834">
          <div className="text-center mb-8 sm:mb-12" data-id="element-25835">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-3 sm:mb-4" data-id="element-25836">
              <Package className="w-4 h-4 mr-1.5" data-id="element-25837" />
              Пакеты отчетов
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-25838">
              Экономьте до 30% при покупке пакета
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-25839">
              Оптимальное решение для регулярного использования. Покупайте оптом
              — платите меньше за каждый отчёт.
            </p>
          </div>

          {subDiscount > 0 && <div className="max-w-3xl mx-auto mb-6 sm:mb-8 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4" data-id="element-25840">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-25841">
                <Crown className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-25842" />
                <div className="text-sm sm:text-base" data-id="element-25843">
                  <span className="font-bold text-slate-900" data-id="element-25844">
                    Ваш тариф: {currentPlan}
                  </span>
                  <span className="text-sm text-slate-600 ml-1 sm:ml-2" data-id="element-25845">
                    — скидка{' '}
                    <span className="font-bold text-indigo-600" data-id="element-25846">
                      {subDiscount}%
                    </span>{' '}
                    на все пакеты
                  </span>
                </div>
              </div>
            </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto" data-id="element-25847">
            {/* Пакет 20 */}
            <Card padding="md" className="flex flex-col border-slate-200 hover:shadow-lg transition-shadow sm:last:col-span-2 md:last:col-span-1" data-id="element-25848">
              <div className="mb-4 sm:mb-6" data-id="element-25849">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2" data-id="element-25850">
                  20 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6" data-id="element-25851">
                  Оптимально для регулярного использования
                </p>
                <div className="mb-2" data-id="element-25852">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-slate-400 line-through" data-id="element-25853">
                      76 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-25854">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-25855">
                      {getDiscountedPackagePrice(76000, 20).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-25856">
                      <Crown className="w-3 h-3" data-id="element-25857" />-{subDiscount}% (
                      {(76000 - getDiscountedPackagePrice(76000, 20)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-25858">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-25859">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-25860">
                    <span className="text-slate-500" data-id="element-25861">За 1 отчет:</span>
                    <span className="font-semibold text-slate-900" data-id="element-25862">
                      {Math.round(getDiscountedPackagePrice(76000, 20) / 20).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-25863">
                    <span className="text-slate-500" data-id="element-25864">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-25865">
                      {(90000 - getDiscountedPackagePrice(76000, 20)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-3" data-id="element-25866">
                  {['Любые отчеты на выбор', 'Использование в любое время', 'Доступ без ограничений по времени'].map((f, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-25867">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-25868" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="outline" fullWidth size="lg" onClick={() => handleBuyPackage({
              title: 'Пакет 20 отчетов',
              desc: 'Оптимально для регулярного использования',
              price: getDiscountedPackagePrice(76000, 20),
              oldPrice: subDiscount > 0 ? 76000 : undefined
            })} data-id="element-25869">
                Купить пакет
              </Button>
            </Card>

            {/* Пакет 50 */}
            <Card padding="md" className="flex flex-col border-indigo-200 shadow-xl shadow-indigo-500/10 relative sm:col-span-2 md:col-span-1 md:-translate-y-4" data-id="element-25870">
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10" data-id="element-25871">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg flex items-center gap-1 sm:gap-1.5 whitespace-nowrap" data-id="element-25872">
                  <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-25873" />
                  Популярно
                </span>
              </div>

              <div className="mb-4 sm:mb-6 pt-2" data-id="element-25874">
                <h3 className="text-xl sm:text-2xl font-bold text-indigo-900 mb-1 sm:mb-2" data-id="element-25875">
                  50 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-indigo-600/80 mb-4 sm:mb-6" data-id="element-25876">
                  Лучший выбор для активных пользователей
                </p>
                <div className="mb-2" data-id="element-25877">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-indigo-300 line-through" data-id="element-25878">
                      180 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-25879">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-indigo-900" data-id="element-25880">
                      {getDiscountedPackagePrice(180000, 50).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-25881">
                      <Crown className="w-3 h-3" data-id="element-25882" />-{subDiscount}% (
                      {(180000 - getDiscountedPackagePrice(180000, 50)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-25883">
                <div className="bg-indigo-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-25884">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-25885">
                    <span className="text-indigo-600/80" data-id="element-25886">За 1 отчет:</span>
                    <span className="font-semibold text-indigo-900" data-id="element-25887">
                      {Math.round(getDiscountedPackagePrice(180000, 50) / 50).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-25888">
                    <span className="text-indigo-600/80" data-id="element-25889">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-25890">
                      {(225000 - getDiscountedPackagePrice(180000, 50)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-3" data-id="element-25891">
                  {['Любые отчеты', 'Быстрое масштабирование работы', 'Приоритетная обработка'].map((f, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-25892">
                      <Check className="w-4 h-4 text-indigo-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-25893" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="primary" fullWidth size="lg" className="shadow-md shadow-indigo-500/20" onClick={() => handleBuyPackage({
              title: 'Пакет 50 отчетов',
              desc: 'Лучший выбор для активных пользователей',
              price: getDiscountedPackagePrice(180000, 50),
              oldPrice: subDiscount > 0 ? 180000 : undefined
            })} data-id="element-25894">
                Купить пакет
              </Button>
            </Card>

            {/* Пакет 100 */}
            <Card padding="md" className="flex flex-col border-slate-200 hover:shadow-lg transition-shadow" data-id="element-25895">
              <div className="mb-4 sm:mb-6" data-id="element-25896">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2" data-id="element-25897">
                  100 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6" data-id="element-25898">
                  Максимальная выгода для профессионалов
                </p>
                <div className="mb-2" data-id="element-25899">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-slate-400 line-through" data-id="element-25900">
                      315 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-25901">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-25902">
                      {getDiscountedPackagePrice(315000, 100).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-25903">
                      <Crown className="w-3 h-3" data-id="element-25904" />-{subDiscount}% (
                      {(315000 - getDiscountedPackagePrice(315000, 100)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-25905">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-25906">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-25907">
                    <span className="text-slate-500" data-id="element-25908">За 1 отчет:</span>
                    <span className="font-semibold text-slate-900" data-id="element-25909">
                      {Math.round(getDiscountedPackagePrice(315000, 100) / 100).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-25910">
                    <span className="text-slate-500" data-id="element-25911">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-25912">
                      {(450000 - getDiscountedPackagePrice(315000, 100)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 sm:space-y-3" data-id="element-25913">
                  {['Все типы отчетов', 'Максимальная экономия', 'Подходит для бизнеса'].map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-slate-700" data-id="element-25914">
                      <Check className="w-4 h-4 text-emerald-500 mr-2 sm:mr-2.5 mt-0.5 flex-shrink-0" data-id="element-25915" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="outline" fullWidth size="lg" onClick={() => handleBuyPackage({
              title: 'Пакет 100 отчетов',
              desc: 'Максимальная выгода для профессионалов',
              price: getDiscountedPackagePrice(315000, 100),
              oldPrice: subDiscount > 0 ? 315000 : undefined
            })} data-id="element-25916">
                Купить пакет
              </Button>
            </Card>
          </div>

          <div className="text-center mt-6 sm:mt-10" data-id="element-25917">
            <p className="text-xs sm:text-sm text-slate-500 font-medium bg-white inline-block px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-25918">
              Отчеты из пакета списываются автоматически при покупке услуг
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-6 sm:py-8 bg-white border-y border-slate-100" data-id="element-25919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25920">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-id="element-25921">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-25922" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-25923" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-25924" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-25925" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-25926">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-25927">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-25928">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-25929">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-25930">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25931">
          <div className="text-center mb-8" data-id="element-25932">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-25933">
              <HelpCircle className="w-5 h-5" data-id="element-25934" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25935">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-25936">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-25937">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-25938" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-25939" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-25940" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-25941" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-25942" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-25943" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-25944">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-25945">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-25946">
                  <div className="text-sm font-bold text-slate-900" data-id="element-25947">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25948">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-25949" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-25950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25951">
          <div className="text-center mb-8" data-id="element-25952">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25953">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-25954" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-10 sm:py-14 lg:py-16 bg-slate-900 relative overflow-hidden" data-id="element-25955">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-25956" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-25957">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-25958">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-25959" />
          </motion.div>
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-white mb-3 sm:mb-4" data-id="element-25960">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-sm sm:text-base text-indigo-100/80 mb-6 sm:mb-8 max-w-xl mx-auto" data-id="element-25961">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-25962">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-25963" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-25964">
            <span className="flex items-center" data-id="element-25965">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-25966" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-25967">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-25968" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}